import type { GoogleAuth } from "../net/googleAuth.ts";
import { NAME_MAX_LENGTH, ROOM_CODE_LENGTH, isRoomCode, normalizeRoomCode, sanitizeName, type CharacterManifest } from "@arena/shared";

/** Home screen (GDD §7.1): name, create a room, join one with a code, or train alone against a bot. */
export class HomeScreen {
  onCreate: (name: string) => void = () => {};
  onJoin: (code: string, name: string) => void = () => {};
  /** A room of one's own, a bot in it, straight into the training arena. */
  onSolo: (name: string) => void = () => {};

  private readonly root = element("home");
  private readonly form = element<HTMLFormElement>("home-form");
  private readonly name = element<HTMLInputElement>("home-name");
  private readonly code = element<HTMLInputElement>("home-code");
  private readonly create = element<HTMLButtonElement>("home-create");
  private readonly join = element<HTMLButtonElement>("home-join");
  private readonly error = element("home-error");

  private joining = false;
  private readonly joinPanel = element("home-join-panel");
  private readonly chooseJoin = element<HTMLButtonElement>("home-choose-join");
  private readonly solo = element<HTMLButtonElement>("home-solo");
  private readonly back = element<HTMLButtonElement>("home-back");
  private readonly actions = element("home-actions");
  private readonly google = element("home-google");
  private readonly googleButton = element("home-google-button");
  private readonly account = element("home-account");
  private readonly accountName = element("home-account-name");
  private readonly accountAvatar = element<HTMLImageElement>("home-account-avatar");
  private readonly accountLevel = element("home-account-level");
  private readonly signOut = element<HTMLButtonElement>("home-signout");
  private auth: GoogleAuth | null = null;

  /** `champions`: their portraits line the bottom of the screen. */
  constructor(champions: readonly CharacterManifest[]) {
    element("home-frieze").replaceChildren(...champions.map((c) => {
      const portrait = document.createElement("img");
      portrait.src = `/proto2d/portraits/closeups/${c.id}.png`;
      portrait.alt = "";
      portrait.draggable = false;
      portrait.style.setProperty("--accent-color", c.look.accent);
      return portrait;
    }));
    this.solo.addEventListener("click", () => {
      const name = this.readName();
      if (name) this.onSolo(name);
    });
    this.signOut.addEventListener("click", () => this.auth?.signOut());
    this.name.maxLength = NAME_MAX_LENGTH;
    this.code.maxLength = ROOM_CODE_LENGTH;
    this.create.addEventListener("click", () => {
      const name = this.readName();
      if (name) this.onCreate(name);
    });
    this.chooseJoin.addEventListener("click", () => {
      if (!this.readName()) return;
      this.showError("");this.setJoining(true);this.code.focus();
    });
    this.back.addEventListener("click", () => {
      this.setJoining(false);this.showError("");this.chooseJoin.focus();
    });
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      const name = this.readName();
      if (!name) return;
      // Enter in the nickname field must not silently choose an action.
      if (!this.joining) { this.create.focus(); return; }
      const code = normalizeRoomCode(this.code.value);
      if (!isRoomCode(code)) {
        this.code.setAttribute("aria-invalid","true");
        this.showError(`Il codice è di ${ROOM_CODE_LENGTH} caratteri, per esempio K7QPM.`);
        this.code.focus();
        return;
      }
      this.code.value = code;
      this.onJoin(code, name);
    });
    this.code.addEventListener("input", () => {
      this.code.value = normalizeRoomCode(this.code.value).slice(0, ROOM_CODE_LENGTH);
      this.code.removeAttribute("aria-invalid");
    });
    this.name.addEventListener("input", () => this.name.removeAttribute("aria-invalid"));
    // Game shortcuts must not consume spaces or arrows while editing a field.
    this.form.addEventListener("keydown", event => event.stopPropagation());
    this.form.addEventListener("keyup", event => event.stopPropagation());
  }

  show(name: string, code = "", error = ""): void {
    document.body.classList.add("at-menu");
    this.name.value = sanitizeName(name, "");
    this.code.value = normalizeRoomCode(code).slice(0, ROOM_CODE_LENGTH);
    this.name.removeAttribute("aria-invalid");this.code.removeAttribute("aria-invalid");
    this.setJoining(Boolean(code));
    this.renderAuth();
    this.showError(error);
    this.setBusy(false);
    this.root.hidden = false;
    // With a code from an invite link the next step is "Entra".
    (this.name.value ? code ? this.join : this.create : this.name).focus({ preventScroll: true });
  }

  /** Sign-in with Google is required: the room actions wait for it. */
  useAuth(auth: GoogleAuth): void {
    this.auth = auth;
    auth.onChange = () => this.renderAuth();
    auth.onError = (message) => this.showError(message);
    void auth.renderButton(this.googleButton).catch(() => this.showError("Accesso con Google non disponibile: riprova più tardi."));
    this.renderAuth();
  }

  private renderAuth(): void {
    const auth = this.auth;
    const out = auth !== null && !auth.signedIn;
    this.google.hidden = !out;
    this.account.hidden = auth === null || out;
    this.name.closest("label")!.hidden = out;
    if (out) {
      this.actions.hidden = true;
      this.joinPanel.hidden = true;
      return;
    }
    this.setJoining(this.joining);
    if (auth) {
      this.accountName.textContent = auth.firstName || "Google";
      this.accountLevel.textContent = `Livello ${auth.profile?.level ?? 1}`;
      const avatar = auth.profile?.avatar;
      this.accountAvatar.hidden = !avatar;
      if (avatar) this.accountAvatar.src = avatar;
      else this.accountAvatar.removeAttribute("src");
      // The Google first name is a starting point; the nickname stays the player's choice.
      if (!this.name.value && auth.firstName) this.name.value = sanitizeName(auth.firstName, "");
    }
  }

  hide(): void {
    document.body.classList.remove("at-menu");
    this.root.hidden = true;
  }

  setBusy(busy: boolean): void {
    for (const control of [this.name,this.code,this.create,this.chooseJoin,this.solo,this.join,this.back]) control.disabled = busy;
    this.form.setAttribute("aria-busy",String(busy));
    if (busy) this.showError("");
  }

  showError(message: string): void {
    this.error.textContent = message;
    this.error.hidden = !message;
  }

  private setJoining(joining: boolean):void {
    this.joining=joining;this.actions.hidden=joining;this.joinPanel.hidden=!joining;
    this.code.required=joining;
  }

  private readName(): string | null {
    const name = sanitizeName(this.name.value, "");
    if (!name) {
      this.name.setAttribute("aria-invalid","true");
      this.showError("Scegli un nickname per entrare nell’arena.");
      this.name.focus();
      return null;
    }
    this.name.value=name;this.name.removeAttribute("aria-invalid");
    return name;
  }
}

function element<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} mancante in index.html`);
  return el as T;
}
